import { useState, useEffect, useCallback } from 'react';
import { followUpsAPI } from '@/lib/api';
import { useNavigate } from 'react-router-dom';
import { useSettings } from '@/contexts/SettingsContext';
import { Clock, AlertTriangle, CheckCircle2, Phone, Mail, Calendar, Building2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';

const TYPE_ICONS = { call: Phone, email: Mail, meeting: Calendar, visit: Building2 };

export default function FollowUpsPage() {
  const [followUps, setFollowUps] = useState([]);
  const [total, setTotal] = useState(0);
  const [tab, setTab] = useState('pending');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchFollowUps = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (tab !== 'all') params.status = tab;
      const res = await followUpsAPI.list(params);
      setFollowUps(res.data.follow_ups);
      setTotal(res.data.total);
    } catch (e) {  } finally { setLoading(false); }
  }, [tab]);

  useEffect(() => { fetchFollowUps(); }, [fetchFollowUps]);

  const markDone = async (id) => {
    await followUpsAPI.update(id, { status: 'completed' });
    toast.success('Als erledigt markiert');
    fetchFollowUps();
  };

  const overdue = followUps.filter(f => new Date(f.due_date) < new Date() && f.status === 'pending' && new Date(f.due_date).toDateString() !== new Date().toDateString());
  const todayDue = followUps.filter(f => new Date(f.due_date).toDateString() === new Date().toDateString() && f.status === 'pending');
  const upcoming = followUps.filter(f => new Date(f.due_date) > new Date() && f.status === 'pending' && new Date(f.due_date).toDateString() !== new Date().toDateString());
  const completed = followUps.filter(f => f.status === 'completed');

  return (
    <div className="space-y-5 fade-in" data-testid="followups-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Nachfassaktionen</h1>
          <p className="text-muted-foreground text-sm">{total} gesamt</p>
        </div>
        <div className="flex items-center gap-2">
          {overdue.length > 0 && (
            <Badge variant="destructive" className="gap-1.5 px-3 py-1">
              <AlertTriangle className="w-3.5 h-3.5" />
              {overdue.length} überfällig
            </Badge>
          )}
          {todayDue.length > 0 && (
            <Badge variant="outline" className="gap-1.5 px-3 py-1 border-amber-400 text-amber-600">
              <Clock className="w-3.5 h-3.5" />
              {todayDue.length} heute
            </Badge>
          )}
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="pending" data-testid="tab-pending">
            Offen {(overdue.length + todayDue.length + upcoming.length) > 0 && `(${overdue.length + todayDue.length + upcoming.length})`}
          </TabsTrigger>
          <TabsTrigger value="completed" data-testid="tab-completed">
            Erledigt {completed.length > 0 && `(${completed.length})`}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="space-y-4 mt-4">
          {loading && (
            <div className="space-y-2">
              {[...Array(3)].map((_, i) => (
                <div key={`skeleton-fu-${i}`} className="h-20 bg-card border border-border/40 animate-pulse rounded-lg" />
              ))}
            </div>
          )}
          {!loading && overdue.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-sm font-semibold text-destructive flex items-center gap-2"><AlertTriangle className="w-4 h-4" />Überfällig ({overdue.length})</h3>
              {overdue.map((fu) => <FollowUpCard key={fu.id} fu={fu} isOverdue navigate={navigate} onMarkDone={markDone} />)}
            </div>
          )}
          {!loading && todayDue.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-sm font-semibold text-amber-600 flex items-center gap-2"><Clock className="w-4 h-4" />Heute fällig ({todayDue.length})</h3>
              {todayDue.map((fu) => <FollowUpCard key={fu.id} fu={fu} isToday navigate={navigate} onMarkDone={markDone} />)}
            </div>
          )}
          {!loading && upcoming.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-sm font-semibold flex items-center gap-2"><Calendar className="w-4 h-4 text-muted-foreground" />Anstehend ({upcoming.length})</h3>
              {upcoming.map((fu) => <FollowUpCard key={fu.id} fu={fu} navigate={navigate} onMarkDone={markDone} />)}
            </div>
          )}
          {!loading && overdue.length === 0 && todayDue.length === 0 && upcoming.length === 0 && (
            <div className="text-center py-16 text-muted-foreground">
              <CheckCircle2 className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p className="text-sm">Alles erledigt! Keine offenen Nachfassaktionen.</p>
            </div>
          )}
        </TabsContent>

        <TabsContent value="completed" className="space-y-2 mt-4">
          {completed.map((fu) => <FollowUpCard key={fu.id} fu={fu} completed navigate={navigate} />)}
          {completed.length === 0 && !loading && (
            <p className="text-sm text-muted-foreground text-center py-8">Keine erledigten Nachfassaktionen</p>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function FollowUpCard({ fu, isOverdue, isToday: isTodayProp, completed, navigate, onMarkDone }) {
  const Icon = TYPE_ICONS[fu.type] || Clock;
  const { formatDate } = useSettings();
  const dueDate = new Date(fu.due_date);
  const isToday = isTodayProp || dueDate.toDateString() === new Date().toDateString();
  const cardHighlight = isOverdue ? 'border-red-400 bg-destructive/5 shadow-md shadow-red-100/50' : isTodayProp ? 'border-amber-300 bg-amber-50/30' : '';

  return (
    <Card className={`border-border/60 ${cardHighlight} hover:bg-muted/20 transition-colors`} data-testid={`followup-${fu.id}`}>
      <CardContent className="p-4 flex items-center gap-4">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
          completed ? 'bg-green-100 dark:bg-green-900/30' :
          isOverdue ? 'bg-red-100 dark:bg-red-900/30' :
          isTodayProp ? 'bg-amber-100 dark:bg-amber-900/30' :
          'bg-muted'
        }`}>
          {completed ? <CheckCircle2 className="w-4 h-4 text-green-600 dark:text-green-400" /> :
           isOverdue ? <AlertTriangle className="w-4 h-4 text-red-500" /> :
           isTodayProp ? <Clock className="w-4 h-4 text-amber-600" /> :
           <Icon className="w-4 h-4 text-muted-foreground" />}
        </div>
        <div className="flex-1 min-w-0 cursor-pointer" onClick={() => navigate(`/leads/${fu.lead_id}`)}>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium capitalize">{fu.type === 'call' ? 'Anruf' : fu.type === 'email' ? 'E-Mail' : fu.type === 'meeting' ? 'Termin' : fu.type === 'visit' ? 'Besuch' : fu.type}</span>
            <span className="text-xs text-muted-foreground">mit</span>
            <span className="text-sm font-medium">{fu.lead_name}</span>
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            <span className={`text-xs ${isOverdue ? 'text-red-500 font-bold' : isToday ? 'text-amber-600 font-medium' : 'text-muted-foreground'}`}>
              {isOverdue ? 'ÜBERFÄLLIG — ' : ''}{isToday ? 'Heute' : formatDate(fu.due_date)} {dueDate.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
            </span>
            {fu.notes && <span className="text-xs text-muted-foreground truncate max-w-[200px]">— {fu.notes}</span>}
          </div>
        </div>
        {!completed && onMarkDone && (
          <Button size="sm" variant={isOverdue ? "destructive" : isTodayProp ? "default" : "outline"} className="h-8 text-xs gap-1.5 shadow-sm flex-shrink-0" onClick={() => onMarkDone(fu.id)} data-testid={`complete-fu-${fu.id}`}>
            <CheckCircle2 className="w-3.5 h-3.5" /> Erledigt
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
